import { unzipSync } from 'fflate';

export type MapleMarketModuleType = 'web' | 'wasm' | 'engine-plugin';
export type MapleMarketModuleLoader = 'bundle' | 'package';

export type MapleMarketModuleManifest = {
  name: string;
  version: string;
  description?: string;
  author?: string;
  categories?: string[];
  permissions?: string[];
  type: MapleMarketModuleType;
  loader?: MapleMarketModuleLoader;
  assets?: string;
  entry?: {
    html?: string;
    js?: string;
    wasm?: string;
    plugin?: string;
  };
  icons?: { small?: string; large?: string };
  screenshots?: string[];
  marketplace?: {
    sku?: string;
    packageType?: string;
    installedAt?: string;
  };
};

export type ExtractedMapleMarketPackageFile = {
  relativePath: string;
  fullPath: string;
  content: string;
  contentType: string;
};

export type ExtractedMapleMarketPackage = {
  manifest: MapleMarketModuleManifest;
  manifestContent: string;
  files: ExtractedMapleMarketPackageFile[];
};

const TEXT_FILE_EXTENSIONS = new Set([
  'css',
  'csv',
  'html',
  'js',
  'json',
  'map',
  'md',
  'mjs',
  'svg',
  'txt',
  'wat',
  'webmanifest',
  'xml',
]);

const CONTENT_TYPES: Record<string, string> = {
  css: 'text/css; charset=utf-8',
  csv: 'text/csv; charset=utf-8',
  gif: 'image/gif',
  htm: 'text/html; charset=utf-8',
  html: 'text/html; charset=utf-8',
  ico: 'image/x-icon',
  jpeg: 'image/jpeg',
  jpg: 'image/jpeg',
  js: 'text/javascript; charset=utf-8',
  json: 'application/json; charset=utf-8',
  map: 'application/json; charset=utf-8',
  md: 'text/markdown; charset=utf-8',
  mjs: 'text/javascript; charset=utf-8',
  mp3: 'audio/mpeg',
  mp4: 'video/mp4',
  ogg: 'audio/ogg',
  png: 'image/png',
  svg: 'image/svg+xml; charset=utf-8',
  txt: 'text/plain; charset=utf-8',
  wav: 'audio/wav',
  wasm: 'application/wasm',
  wat: 'text/plain; charset=utf-8',
  webm: 'video/webm',
  webmanifest: 'application/manifest+json; charset=utf-8',
  webp: 'image/webp',
  woff: 'font/woff',
  woff2: 'font/woff2',
  xml: 'application/xml; charset=utf-8',
};

export function normalizeWindowsPath(path: string) {
  return path.replace(/\//g, '\\').replace(/\\\\+/g, '\\');
}

export function normalizePackageRelativePath(path: string) {
  return path.replace(/\\/g, '/').replace(/^\.\//, '').replace(/\/+/g, '/').trim();
}

export function sanitizeModuleName(name: string) {
  return name.replace(/[\\/:*?"<>|]/g, '').trim() || 'Marketplace App';
}

export function getContentTypeFromPath(path: string) {
  const extension = normalizePackageRelativePath(path).split('.').pop()?.toLowerCase() || '';
  return CONTENT_TYPES[extension] || 'application/octet-stream';
}

export function isTextContentType(contentType: string, path?: string) {
  if (contentType.startsWith('text/')) {
    return true;
  }

  if (contentType.includes('json') || contentType.includes('xml') || contentType.includes('javascript')) {
    return true;
  }

  const extension = normalizePackageRelativePath(path || '').split('.').pop()?.toLowerCase() || '';
  return TEXT_FILE_EXTENSIONS.has(extension);
}

export function resolvePackageRelativePath(fromPath: string, targetPath: string) {
  const normalizedFrom = normalizePackageRelativePath(fromPath);
  const normalizedTarget = normalizePackageRelativePath(targetPath);

  if (!normalizedTarget || normalizedTarget === '.') {
    return normalizedFrom;
  }

  if (normalizedTarget.startsWith('/')) {
    return normalizedTarget.slice(1);
  }

  const fromParts = normalizedFrom.split('/').filter(Boolean);
  if (!normalizedFrom.endsWith('/')) {
    fromParts.pop();
  }

  for (const part of normalizedTarget.split('/')) {
    if (!part || part === '.') {
      continue;
    }

    if (part === '..') {
      if (fromParts.length === 0) {
        throw new Error(`Package path escapes module root: ${targetPath}`);
      }
      fromParts.pop();
      continue;
    }

    fromParts.push(part);
  }

  return fromParts.join('/');
}

export function isExternalModuleUrl(url: string) {
  const value = url.trim();
  return (
    !value ||
    value.startsWith('#') ||
    value.startsWith('data:') ||
    value.startsWith('blob:') ||
    value.startsWith('javascript:') ||
    value.startsWith('mailto:') ||
    value.startsWith('tel:') ||
    /^[a-z][a-z0-9+.-]*:/i.test(value) ||
    value.startsWith('//')
  );
}

function bytesToBase64(bytes: Uint8Array) {
  let binary = '';
  for (let index = 0; index < bytes.length; index += 1) {
    binary += String.fromCharCode(bytes[index]);
  }
  return btoa(binary);
}

function decodeText(bytes: Uint8Array) {
  return new TextDecoder('utf-8').decode(bytes);
}

function assertSafeArchivePath(path: string) {
  const normalized = normalizePackageRelativePath(path);

  if (!normalized) {
    throw new Error('Archive contains an empty entry path.');
  }

  if (normalized.startsWith('/') || /^[a-z]:/i.test(normalized)) {
    throw new Error(`Archive entry uses an absolute path: ${path}`);
  }

  const parts = normalized.split('/');
  if (parts.some((part) => !part || part === '.' || part === '..')) {
    throw new Error(`Archive entry uses an unsafe path: ${path}`);
  }

  return normalized;
}

function validatePackageManifest(
  manifest: MapleMarketModuleManifest,
  relativePaths: Set<string>,
) {
  if (!manifest || typeof manifest !== 'object') {
    throw new Error('Package manifest is invalid.');
  }

  if (typeof manifest.name !== 'string' || !manifest.name.trim()) {
    throw new Error('Package manifest must include a name.');
  }

  if (typeof manifest.version !== 'string' || !manifest.version.trim()) {
    throw new Error('Package manifest must include a version.');
  }

  if (manifest.type !== 'web' && manifest.type !== 'wasm') {
    throw new Error(`Unsupported package manifest type: ${String(manifest.type)}`);
  }

  if (manifest.loader !== 'package') {
    throw new Error('Maple Market .mpak manifests must declare loader: "package".');
  }

  if (!manifest.entry || typeof manifest.entry !== 'object') {
    throw new Error('Package manifest must include an entry object.');
  }

  if (manifest.type === 'web') {
    if (typeof manifest.entry.html !== 'string' || !manifest.entry.html.trim()) {
      throw new Error('Web packages must declare entry.html in manifest.json.');
    }

    const htmlEntry = assertSafeArchivePath(manifest.entry.html);
    if (!relativePaths.has(htmlEntry)) {
      throw new Error(`Web package entry not found: ${manifest.entry.html}`);
    }
  }

  if (manifest.type === 'wasm') {
    if (typeof manifest.entry.wasm !== 'string' || !manifest.entry.wasm.trim()) {
      throw new Error('WASM packages must declare entry.wasm in manifest.json.');
    }

    const wasmEntry = assertSafeArchivePath(manifest.entry.wasm);
    if (!relativePaths.has(wasmEntry)) {
      throw new Error(`WASM package entry not found: ${manifest.entry.wasm}`);
    }
  }

  return manifest;
}

export function extractMapleMarketPackageArchive(
  archiveBytes: Uint8Array,
  rootPath: string,
): ExtractedMapleMarketPackage {
  let unzipped: Record<string, Uint8Array>;

  try {
    unzipped = unzipSync(archiveBytes);
  } catch {
    throw new Error('Failed to unpack .mpak archive.');
  }

  const relativePathSet = new Set<string>();
  const rawEntries = Object.entries(unzipped)
    .filter(([path, bytes]) => !path.endsWith('/') && bytes && bytes.length >= 0)
    .map(([path, bytes]) => ({
      relativePath: assertSafeArchivePath(path),
      bytes,
    }));

  for (const entry of rawEntries) {
    relativePathSet.add(entry.relativePath);
  }

  if (!relativePathSet.has('manifest.json')) {
    throw new Error('Maple Market packages must include a root-level manifest.json.');
  }

  const manifestEntry = rawEntries.find((entry) => entry.relativePath === 'manifest.json');
  if (!manifestEntry) {
    throw new Error('Maple Market packages must include a root-level manifest.json.');
  }

  let manifest: MapleMarketModuleManifest;
  let manifestContent: string;
  try {
    manifestContent = decodeText(manifestEntry.bytes);
    manifest = JSON.parse(manifestContent) as MapleMarketModuleManifest;
  } catch {
    throw new Error('Package manifest.json is not valid JSON.');
  }

  validatePackageManifest(manifest, relativePathSet);

  const normalizedRoot = normalizeWindowsPath(rootPath);
  const files = rawEntries.map((entry) => {
    const contentType = getContentTypeFromPath(entry.relativePath);
    const content = isTextContentType(contentType, entry.relativePath)
      ? decodeText(entry.bytes)
      : `data:${contentType};base64,${bytesToBase64(entry.bytes)}`;

    return {
      relativePath: entry.relativePath,
      fullPath: normalizeWindowsPath(`${normalizedRoot}\\${entry.relativePath.replace(/\//g, '\\')}`),
      content,
      contentType,
    } satisfies ExtractedMapleMarketPackageFile;
  });

  return {
    manifest,
    manifestContent,
    files,
  };
}
